import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// -----------------------------------------------------------------------------
// Silence html5-qrcode's benign teardown noise.
// When BarcodeScanner unmounts, the library removes the <video> element while
// its internal videoElement.play() promise is still pending, which surfaces as
// an uncaught rejection. It's harmless — the camera is being torn down anyway —
// but React's error overlay treats it as a runtime error. Swallow *only* this
// specific message pattern; anything else still bubbles up as before.
// -----------------------------------------------------------------------------
const _isBenignPlayInterrupt = (reason) => {
  const msg = (reason && (reason.message || String(reason))) || "";
  return (
    /play\(\)[^]*interrupted[^]*media was removed from the document/i.test(msg) ||
    /The play\(\) request was interrupted/i.test(msg)
  );
};

if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (ev) => {
    if (_isBenignPlayInterrupt(ev.reason)) {
      ev.preventDefault();
      if (process.env.NODE_ENV !== "production") {
        console.debug("[silenced] play() interrupted on camera teardown");
      }
    }
  });
  window.addEventListener("error", (ev) => {
    if (_isBenignPlayInterrupt(ev.error || ev.message)) {
      ev.preventDefault();
    }
  });
}
// -----------------------------------------------------------------------------

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
